import React, { useState, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import TextBox from 'devextreme-react/text-box';
import {List} from 'devextreme-react/list';
import debounce from 'debounce';
import { useAddressVerificationApiService } from '../../services';
import './address-auto-complete.scss';
import { showError } from '../../helpers/ui';

const AddressAutoComplete = function (props, ref) {
  const [items, setItems] = useState([]);
  const { getAutoCompleteAddresses } = useAddressVerificationApiService();
  const { updateAddressFields, city, province } = props;
  const [ addressVerified, setAddressVerified ] = useState(props.addressVerified)

  const textBoxRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getAddress() {
      return {addressVerified: addressVerified };
    },
  }));

  // The parent shows the verified state (e.g. a badge by the section heading).
  function setVerified(verified) {
    setAddressVerified(verified);
    props.onVerifiedChange?.(verified);
  }

  async function handleChange(e) {
    if (typeof e.event !== 'undefined') {
      // Typing changes nothing in the form data until a suggestion is picked,
      // so tell the parent the user has started editing the address.
      props.onEdit?.();
      if (addressVerified) setVerified(false);
      if (e.value === '') {
        setItems([]);
      } else {
        debouncedFetchData();
      }
    }
  }

  async function handleSelectionAsync(place_id) {
    try {
      const address = items.find(x => x.place_id === place_id);
      if (address) {

        textBoxRef?.current?.instance().option('value', address.fullAddress.address);

        updateAddressFields({
          address: address.fullAddress.address,
          city: address.fullAddress.city,
          postalCode: address.fullAddress.pc,
          province: address.fullAddress.prov,
        });
        // A picked suggestion is a verified address.
        setVerified(true);
      }
    } catch {
      showError('Error in getting the address.', 'error', 2000);
    }
  }

  const debouncedFetchData = useMemo(
    () => debounce(async () => {
      try {
        const searchValue = textBoxRef?.current?.instance().option('value');
        if (searchValue) {
          const data = await getAutoCompleteAddresses(searchValue, city, province);
          setItems(data);
        }
      } catch (error) {
        showError('Error in getting the addresses.');
      }
    }, 150),
    [getAutoCompleteAddresses, city, province]
  );

  function itemRender(item) {
    return (<div className="address-list-item">
      {item.description}
    </div>);
  }

  const { autoCompleteTextBoxId } = props;
  return (
    <div className="address-autocomplete-container-wrapper">
      <div className="address-autocomplete-container">
        <TextBox
          ref={textBoxRef}
          id={autoCompleteTextBoxId}
          showClearButton
          placeholder="Unit#-Street Number Street Name City"
          valueChangeEvent="keyup"
          defaultValue={props.value}
          onValueChanged={
            async (e) => {
              await handleChange(e)
            }
          }
          onKeyDown={
            (e) => {
              if (e.event.key === "Escape") {
                setItems([]);
              }
              if (e.event.key === "Tab") {
                //ToDO: move the focus to the list when we press tab on the text box.
              }
            }
          }
          inputAttr={{ 'style': "text-transform: uppercase" }}
        />
        <div className="address-autocomplete-container-address-list">
          <List
            showScrollbar={"always"}
            height={Math.min(300, 35 * (items.length + 1))}
            keyExpr="place_id"
            dataSource={items}
            visible={items.length > 0}
            itemRender={itemRender}
            selectionMode="single"
            onSelectionChanged={
              async (e) => {
                setItems([]);
                if (e.addedItems.length === 0) return;
                await handleSelectionAsync(e.addedItems[0].place_id);
              }
            }
          />
        </div>
      </div>
    </div>
  );
}

export default forwardRef(AddressAutoComplete);

